"use client";

import React, { useEffect, useRef, useState } from "react";
import { Flex, Text } from "@radix-ui/themes";
import { AuctionType, BiddingSchedule, BiddingScheduleRange, HighestBid, Lot } from "@prisma/client";
import LotDescription from "../lots/[lotId]/LotDescription";
import BiddingCard from "../lots/[lotId]/BiddingCard";
import LiveCloseTimer from "@/app/admin/auctions/[auctionId]/live-close/LiveCloseTimer";
import { LiveCloseState, useLiveClose } from "@/app/admin/auctions/[auctionId]/live-close/LiveCloseContext";
import BidderFairWarningBanner from "./BidderFairWarningBanner";
import { useSession } from "next-auth/react";
import FloorBidRecorderBiddingCard from "./FloorBidRecorderBiddingCard";

interface Props {
  lot: Lot;
  highestBid: HighestBid | null;
  expired: boolean;
  biddingSchedule: BiddingSchedule & { BiddingScheduleRange: BiddingScheduleRange[] };
  auctionStart: Date;
  onNextLot: () => void;
}

const HybridLiveLotPanel = ({ lot, highestBid, expired, biddingSchedule, auctionStart, onNextLot }: Props) => {
  const { data: session }: any = useSession();
  const isFloorBidder = /__floorbidder\d+__/.test(session?.userId ?? '');
  const { liveCloseState, setLiveCloseState, auctionState: liveAuctionState, isFinalLot } = useLiveClose();

  // Ensure we paginate only once per lot when state transitions to CLOSED
  const paginatedLotIdRef = useRef<number | null>(null);

  // Reset flag when the lot changes
  useEffect(() => {
    paginatedLotIdRef.current = null;
  }, [lot.id]);

  useEffect(() => {
    if (liveCloseState === LiveCloseState.READY_TO_ADVANCE && paginatedLotIdRef.current !== lot.id) {
      paginatedLotIdRef.current = lot.id;
      onNextLot();
      setLiveCloseState(LiveCloseState.ACTIVE);
    }
  }, [liveCloseState, lot.id, onNextLot, isFinalLot, setLiveCloseState]);

  return (
    <Flex
      direction="column"
      className="w-full ml-0 items-center lg:items-start space-y-5"
    >
      {/* Fair warning banner (absolute, responsive placement) */}
      <BidderFairWarningBanner isVisible={liveCloseState === LiveCloseState.FAIR_WARNING} />
      <Flex className="w-full flex-col gap-3 items-center lg:items-start">
        <Flex className="self-start mb-1" direction="column">
          <Flex className="w-full" justify="between">
            <LiveCloseTimer />
          </Flex>
          <Text className="text-gray-700 text-lg font-semibold">Lot {lot.catalogNumber}</Text>
          <Text className="mt-4 text-[26px] sm:text-[30px] font-semibold">{lot.title}</Text>
          <Flex direction="column" gap="5" className="mt-4">
            <LotDescription lot={lot} />
          </Flex>
        </Flex>
        {isFloorBidder ? (
          <FloorBidRecorderBiddingCard
            biddingSchedule={biddingSchedule}
            lot={lot}
            expired={expired}
            currentBid={highestBid}
            auctionStart={auctionStart}
            auctionEnd={lot.endDate}
            auctionState={liveAuctionState}
            auctionType={AuctionType.HYBRID}
          />
        ) : (
          <BiddingCard
            biddingSchedule={biddingSchedule}
            lot={lot}
            expired={expired}
            currentBid={highestBid}
            auctionStart={auctionStart}
            auctionEnd={lot.endDate}
            auctionState={liveAuctionState}
            auctionType={AuctionType.HYBRID}
          />)
        }
      </Flex>
    </Flex>
  );
};

export default HybridLiveLotPanel;


